<template>
  <div class="find-sound-page">
    <el-row>
      <el-col :span="12"><Title title="找音效会话"></Title></el-col>
      <el-col :span="12">
        <div class="header-actions">
          <div
            v-if="unreadQueryCount > 0"
            class="header-unread"
          >
            <span class="dot"></span>
            <span>{{ unreadQueryCount }} 个会话有新消息（{{ unreadMessageCount }} 条）</span>
          </div>
          <el-button icon="el-icon-refresh" @click="refreshAll">刷新</el-button>
          <el-button
            type="danger"
            icon="el-icon-delete"
            :disabled="!selectedFindSoundId"
            @click="handleDeleteCurrent"
          >
            删除会话
          </el-button>
        </div>
      </el-col>
    </el-row>

    <el-card class="box-card filter-card">
      <el-row :gutter="16" type="flex" align="middle">
        <el-col :span="5">
          <el-input
            v-model="search_op.search_key"
            placeholder="关键词"
            clearable
            @change="getList"
          >
            <template slot="prepend">内容</template>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-input
            v-model="search_op.user_name"
            placeholder="用户名"
            clearable
            @change="getList"
          >
            <template slot="prepend">用户</template>
          </el-input>
        </el-col>
        <el-col :span="6">
          <el-date-picker
            v-model="search_op.create_time_range"
            type="daterange"
            value-format="timestamp"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            @change="getList"
          />
        </el-col>
      </el-row>
    </el-card>

    <el-card class="box-card dialog-card">
      <div class="dialog-layout">
        <aside class="dialog-sidebar">
          <div class="dialog-sidebar-title">
            <span>会话列表</span>
            <span class="count">{{ total }} 条</span>
          </div>
          <div v-loading="listLoading" class="dialog-list">
            <template v-if="list.length">
              <div
                v-for="item in list"
                :key="item.id"
                class="dialog-item"
                :class="{ active: selectedFindSoundId === item.id }"
                @click="selectConversation(item.id)"
              >
                <div class="dialog-item-top">
                  <div class="item-title">
                    <span class="username">{{ item.username || ('用户#' + item.login_user_id) }}</span>
                    <span
                      v-if="item.has_unread"
                      class="item-unread-dot"
                    ></span>
                  </div>
                  <el-tag v-if="item.reply_status === 'replied'" size="mini" type="success">已回复</el-tag>
                  <el-tag v-else size="mini" type="warning">待回复</el-tag>
                </div>
                <div class="dialog-item-preview">{{ getLatestPreview(item) }}</div>
                <div class="dialog-item-meta">
                  <span>{{ item.time }}</span>
                  <span>
                    {{ item.message_count || 1 }} 条消息
                    <em
                      v-if="item.unread_count > 0"
                      class="meta-unread"
                    >
                      · {{ item.unread_count }} 条未读
                    </em>
                  </span>
                </div>
              </div>
            </template>
            <el-empty v-else description="暂无会话记录" />
          </div>
        </aside>

        <section class="dialog-main">
          <template v-if="currentConversation">
            <div class="dialog-main-header">
              <div class="main-header-left">
                <div class="title-row">
                  <h3>{{ currentConversation.username || ('用户#' + currentConversation.login_user_id) }}</h3>
                  <el-tag v-if="currentConversation.reply_status === 'replied'" size="mini" type="success">已回复</el-tag>
                  <el-tag v-else size="mini" type="warning">待回复</el-tag>
                </div>
                <p>创建时间：{{ currentConversation.time }}</p>
              </div>
              <div class="main-header-right">
                <p>称呼：{{ currentConversation.finder || '未填写' }}</p>
                <p>联系方式：{{ currentConversation.contact_way || '未填写' }}</p>
              </div>
            </div>

            <div ref="messageContainer" v-loading="detailLoading" class="dialog-messages">
              <template v-if="currentMessages.length">
                <div
                  v-for="msg in currentMessages"
                  :key="msg.id + '-' + msg.timestamp + '-' + msg.sender"
                  class="message-row"
                  :class="{ admin: msg.sender === 'admin', user: msg.sender !== 'admin' }"
                >
                  <div class="bubble">
                    <div class="sender">{{ msg.sender === 'admin' ? '官方回复' : '用户提问' }}</div>
                    <div class="content">{{ msg.content }}</div>
                    <div class="time">{{ msg.time_text }}</div>
                  </div>
                </div>
              </template>
              <el-empty v-else description="暂无消息" />
            </div>

            <div class="dialog-editor">
              <el-input
                ref="replyInput"
                v-model="replyContent"
                type="textarea"
                :autosize="{ minRows: 3, maxRows: 6 }"
                placeholder="输入回复内容，支持持续对话（Ctrl + Enter 发送）"
                @keyup.ctrl.enter.native="sendReply"
              />
              <div class="editor-actions">
                <div class="editor-tools">
                  <emoji-picker @select="appendEmoji" />
                  <span class="editor-tip">消息将直接进入该会话，用户端可继续追问。</span>
                </div>
                <el-button
                  type="primary"
                  :loading="sending"
                  :disabled="!selectedFindSoundId"
                  @click="sendReply"
                >
                  发送回复
                </el-button>
              </div>
            </div>
          </template>
          <el-empty v-else description="请先从左侧选择一个会话" />
        </section>
      </div>
    </el-card>

    <el-pagination
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="page_num"
      :page-sizes="[5, 10, 15, 20]"
      :page-size="page_size"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      class="pagination"
    />
  </div>
</template>

<script>
import Title from "./components/Title";
import EmojiPicker from "./components/EmojiPicker";
import apis from "@/api/index";

export default {
  name: "FindSound",
  components: {
    Title,
    EmojiPicker,
  },
  data() {
    return {
      list: [],
      page_num: 1,
      page_size: 5,
      total: 0,
      search_op: {},
      selectedFindSoundId: null,
      currentConversation: null,
      replyContent: "",
      listLoading: false,
      detailLoading: false,
      sending: false,
      unreadStat: {
        total_query_count: 0,
        unread_query_count: 0,
        unread_message_count: 0,
        has_unread: false,
        fetched_at: 0,
      },
      unreadStatReqSeq: 0,
      autoRefreshTimer: null,
      autoRefreshIntervalMs: 45000,
    };
  },
  computed: {
    currentMessages() {
      if (!this.currentConversation || !Array.isArray(this.currentConversation.messages)) {
        return [];
      }
      return this.currentConversation.messages;
    },
    unreadQueryCount() {
      return Number(this.unreadStat.unread_query_count || 0);
    },
    unreadMessageCount() {
      return Number(this.unreadStat.unread_message_count || 0);
    },
  },
  methods: {
    confirm(ob, callback) {
      this.$confirm(ob.msg, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: !ob.type ? "warning" : ob.type,
      })
        .then(() => {
          callback();
        })
        .catch(() => {});
    },
    refreshAll() {
      this.getList(false, true, true);
      this.loadUnreadStat();
    },
    normalizeConversationItem(item) {
      const row = Object.assign({}, item || {});
      row.unread_count = Number(row.unread_count || 0);
      row.has_unread = Boolean(row.has_unread) || row.unread_count > 0;
      row.reply_status = row.reply_status === "replied" ? "replied" : "pending";
      const latestTs = Number(
        row.latest_message && row.latest_message.timestamp
          ? row.latest_message.timestamp
          : row.timestamp || 0
      );
      row._sort_ts = latestTs > 0 ? latestTs : 0;
      return row;
    },
    sortConversationList(list) {
      return (Array.isArray(list) ? list.slice() : []).sort((a, b) => {
        const unreadGap = Number(Boolean(b.has_unread)) - Number(Boolean(a.has_unread));
        if (unreadGap !== 0) return unreadGap;
        const tsGap = Number(b._sort_ts || 0) - Number(a._sort_ts || 0);
        if (tsGap !== 0) return tsGap;
        return Number(b.id || 0) - Number(a.id || 0);
      });
    },
    syncListItemByDetail(detailRow) {
      if (!detailRow || !detailRow.id) return;
      const targetId = Number(detailRow.id);
      const index = this.list.findIndex((item) => Number(item.id) === targetId);
      if (index < 0) return;
      const latestMessage = Array.isArray(detailRow.messages) && detailRow.messages.length
        ? detailRow.messages[detailRow.messages.length - 1]
        : null;
      const merged = Object.assign({}, this.list[index], this.normalizeConversationItem(detailRow), {
        latest_message: latestMessage || this.list[index].latest_message,
      });
      this.$set(this.list, index, merged);
      this.list = this.sortConversationList(this.list);
    },
    applyUnreadStat(payload) {
      const stat = payload || {};
      const serverFetchedAtMs = Number(stat.server_timestamp_ms || 0);
      // 未读状态必须按“服务端生成时间”单向前进，避免旧响应回写导致角标回跳。
      const fetchedAt = serverFetchedAtMs > 0 ? serverFetchedAtMs : Number(stat.fetched_at || Date.now());
      const localFetchedAt = Number(this.unreadStat.fetched_at || 0);
      if (localFetchedAt > 0 && fetchedAt < localFetchedAt) {
        return;
      }
      this.unreadStat = {
        total_query_count: Number(stat.total_query_count || 0),
        unread_query_count: Number(stat.unread_query_count || 0),
        unread_message_count: Number(stat.unread_message_count || 0),
        has_unread: Boolean(stat.has_unread || Number(stat.unread_message_count || 0) > 0),
        fetched_at: fetchedAt,
      };
      window.dispatchEvent(new CustomEvent("admin_find_sound_unread_stat_changed", { detail: this.unreadStat }));
    },
    loadUnreadStat() {
      const reqSeq = ++this.unreadStatReqSeq;
      apis.findSound
        .unreadStat({})
        .then((res) => {
          if (reqSeq !== this.unreadStatReqSeq) return;
          const data = res && res.data ? res.data : {};
          if (data.code !== 1 || !data.data) return;
          this.applyUnreadStat(data.data);
        })
        .catch((err) => {
          if (reqSeq !== this.unreadStatReqSeq) return;
          console.error(err);
        });
    },
    getLatestPreview(item) {
      const latest = item && item.latest_message && item.latest_message.content
        ? item.latest_message.content
        : item && item.description
          ? item.description
          : "";
      return latest.length > 46 ? `${latest.slice(0, 46)}...` : latest;
    },
    getCurrentConversationLatestTs() {
      // 当前会话优先取“最后一条消息时间”，没有消息则回退到会话主记录时间。
      // 这个时间戳用于自动刷新时判断“详情是否真的需要重拉”。
      if (!this.currentConversation) return 0;
      const messages = Array.isArray(this.currentConversation.messages) ? this.currentConversation.messages : [];
      if (messages.length > 0) {
        const lastMsg = messages[messages.length - 1] || {};
        return Number(lastMsg.timestamp || 0);
      }
      const latestMessage = this.currentConversation.latest_message || {};
      return Number(latestMessage.timestamp || this.currentConversation.timestamp || 0);
    },
    getList(toS = true, keepSelection = true, forceConversationRefresh = false) {
      if (toS) {
        this.page_num = 1;
      }
      this.listLoading = true;
      const op = {
        page_num: this.page_num,
        page_size: this.page_size,
      };
      Object.assign(op, this.search_op);
      apis.findSound
        .read(op)
        .then((res) => {
          const payload = res.data && res.data.data ? res.data.data : {};
          this.list = this.sortConversationList(
            (payload.list || []).map((item) => this.normalizeConversationItem(item))
          );
          this.total = payload.count || 0;

          if (!this.list.length) {
            this.selectedFindSoundId = null;
            this.currentConversation = null;
            return;
          }

          let nextId = null;
          if (keepSelection && this.selectedFindSoundId) {
            const exists = this.list.find((item) => item.id === this.selectedFindSoundId);
            if (exists) nextId = this.selectedFindSoundId;
          }
          if (!nextId) {
            nextId = this.list[0].id;
          }
          // 列表刷新后不盲目拉详情，交给 selectConversation 的按需策略决定是否发请求。
          this.selectConversation(nextId, forceConversationRefresh);
        })
        .catch((err) => {
          this.$message({ message: String(err), type: "error" });
        })
        .finally(() => {
          this.listLoading = false;
          this.loadUnreadStat();
        });
    },
    selectConversation(id, forceRefresh = false) {
      if (!id) return;
      const targetId = Number(id);
      const currentId = Number(this.selectedFindSoundId || 0);
      const targetRow = this.list.find((item) => Number(item.id) === targetId) || null;
      if (!forceRefresh && currentId === targetId && this.currentConversation) {
        // 同一会话下，只有“有未读”或“列表最新时间晚于当前详情时间”才重拉详情。
        // 这样可以把自动轮询时的大量无效详情请求砍掉。
        const hasUnread = targetRow ? Boolean(targetRow.has_unread) : false;
        const targetTs = targetRow ? Number(targetRow._sort_ts || 0) : 0;
        const currentTs = this.getCurrentConversationLatestTs();
        if (!hasUnread && targetTs <= currentTs) {
          return;
        }
      }
      this.selectedFindSoundId = targetId;
      this.fetchConversationDetail(id);
    },
    fetchConversationDetail(id) {
      this.detailLoading = true;
      apis.findSound
        .detail({ id })
        .then((res) => {
          if (res.data.code !== 1) {
            this.$message({ message: res.data.msg || "获取详情失败", type: "error" });
            return;
          }
          this.currentConversation = this.normalizeConversationItem(res.data.data || null);
          this.syncListItemByDetail(this.currentConversation);
          if (this.currentConversation && this.currentConversation.unread_stat) {
            this.applyUnreadStat(this.currentConversation.unread_stat);
          }
          this.$nextTick(() => {
            this.scrollMessageToBottom();
          });
          this.loadUnreadStat();
        })
        .catch((err) => {
          this.$message({ message: String(err), type: "error" });
        })
        .finally(() => {
          this.detailLoading = false;
        });
    },
    scrollMessageToBottom() {
      const box = this.$refs.messageContainer;
      if (!box || typeof box.scrollHeight === "undefined") return;
      box.scrollTop = box.scrollHeight;
    },
    /**
     * 在回复框当前光标处插入 Emoji，并在视图更新后恢复输入焦点。
     *
     * @example 回复“已经为您找到”且光标在末尾，选择“😊”后得到“已经为您找到😊”。
     */
    appendEmoji(emoji) {
      const input = this.$refs.replyInput;
      const textarea = input && (
        input.textarea ||
        (input.$refs && input.$refs.textarea) ||
        (input.$el && input.$el.querySelector("textarea"))
      );
      const value = String(this.replyContent || "");
      const start = textarea && Number.isInteger(textarea.selectionStart)
        ? textarea.selectionStart
        : value.length;
      const end = textarea && Number.isInteger(textarea.selectionEnd)
        ? textarea.selectionEnd
        : start;
      const nextValue = value.slice(0, start) + emoji + value.slice(end);
      if (nextValue.length > 1000) {
        this.$message({ message: "回复最多输入 1000 个字符", type: "warning" });
        return;
      }
      this.replyContent = nextValue;
      this.$nextTick(() => {
        if (!textarea) return;
        const cursor = start + emoji.length;
        textarea.focus();
        textarea.setSelectionRange(cursor, cursor);
      });
    },
    sendReply() {
      if (!this.selectedFindSoundId) {
        this.$message({ message: "请先选择会话", type: "warning" });
        return;
      }
      const content = (this.replyContent || "").trim();
      if (!content) {
        this.$message({ message: "请输入回复内容", type: "warning" });
        return;
      }

      this.sending = true;
      apis.findSound
        .answer({
          id: this.selectedFindSoundId,
          answer: content,
          is_answer: 1,
        })
        .then((res) => {
          if (res.data.code !== 1) {
            this.$message({ message: res.data.msg || "发送失败", type: "error" });
            return;
          }
          this.replyContent = "";
          this.$message({ message: "发送成功", type: "success" });
          this.fetchConversationDetail(this.selectedFindSoundId);
          this.getList(false, true);
          this.loadUnreadStat();
        })
        .catch((err) => {
          this.$message({ message: String(err), type: "error" });
        })
        .finally(() => {
          this.sending = false;
        });
    },
    handleDeleteCurrent() {
      if (!this.selectedFindSoundId) return;
      const row = this.list.find((item) => item.id === this.selectedFindSoundId);
      if (!row) return;

      const msg = row.description || row.id;
      this.confirm({ msg: `确认删除 [${msg}]` }, () => {
        apis.findSound
          .delete({ ids: [row.id] })
          .then((res) => {
            if (res.data.code !== 1) {
              this.$message({ message: res.data.msg, type: "error" });
              return;
            }
            this.$message({ message: "删除成功", type: "success" });
            this.getList(false, false);
          })
          .catch((err) => {
            this.$message({ message: String(err), type: "error" });
          });
      });
    },
    handleSizeChange(val) {
      this.page_size = val;
      this.getList(false, true);
    },
    handleCurrentChange(val) {
      this.page_num = val;
      this.getList(false, true);
    },
    handleAutoRefreshTick() {
      if (!this.isAutoRefreshAllowed()) return;
      if (this.listLoading || this.detailLoading || this.sending) return;
      this.getList(false, true);
    },
    isAutoRefreshAllowed() {
      const path = this.$route && this.$route.path ? String(this.$route.path) : "";
      if (path !== "/sounds/findSound") return false;
      if (typeof document !== "undefined" && document.hidden) return false;
      // 页面可见还不够，窗口失焦时也不应持续轮询，避免后台空转。
      if (typeof document !== "undefined" && typeof document.hasFocus === "function" && !document.hasFocus()) return false;
      return true;
    },
    startAutoRefresh() {
      if (!this.isAutoRefreshAllowed()) {
        this.stopAutoRefresh();
        return;
      }
      this.stopAutoRefresh();
      this.autoRefreshTimer = setInterval(() => {
        this.handleAutoRefreshTick();
      }, this.autoRefreshIntervalMs);
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    },
    syncAutoRefreshState(forceLoad = false) {
      if (!this.isAutoRefreshAllowed()) {
        this.stopAutoRefresh();
        return;
      }
      if (forceLoad) {
        this.getList(false, true);
      }
      this.startAutoRefresh();
    },
    handleVisibilityChange() {
      this.syncAutoRefreshState(false);
    },
    handleWindowFocus() {
      this.syncAutoRefreshState(true);
    },
  },
  activated() {
    this.syncAutoRefreshState(true);
  },
  mounted() {
    this.getList(true, true);
    this.loadUnreadStat();
    this.syncAutoRefreshState(false);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", this.handleWindowFocus);
    }
  },
  deactivated() {
    this.stopAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    if (typeof window !== "undefined") {
      window.removeEventListener("focus", this.handleWindowFocus);
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }
  },
};
</script>

<style lang="scss" scoped>
.find-sound-page {
  padding: 15px;
}

.header-actions {
  width: 100%;
  height: 60px;
  padding-right: 15px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.header-unread {
  margin-right: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #f56c6c;
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #f56c6c;
  }
}

.filter-card {
  margin-bottom: 12px;
}

.dialog-card {
  min-height: 620px;
}

.dialog-layout {
  display: flex;
  gap: 14px;
  min-height: 560px;
}

.dialog-sidebar {
  width: 34%;
  min-width: 340px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dialog-sidebar-title {
  padding: 14px 16px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: #2f3542;
}

.dialog-sidebar-title .count {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
}

.dialog-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.dialog-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
}

.dialog-item:hover {
  border-color: #c6e2ff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.08);
}

.dialog-item.active {
  border-color: #409eff;
  background: #f5f9ff;
}

.dialog-item-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.dialog-item-top .item-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.dialog-item-top .username {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dialog-item-top .item-unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f56c6c;
  flex: 0 0 auto;
}

.dialog-item-preview {
  color: #606266;
  line-height: 1.5;
  min-height: 42px;
  white-space: pre-wrap;
  word-break: break-word;
}

.dialog-item-meta {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  color: #909399;
  font-size: 12px;
}

.dialog-item-meta .meta-unread {
  font-style: normal;
  color: #f56c6c;
}

.dialog-main {
  flex: 1;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dialog-main-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  border-bottom: 1px solid #ebeef5;
  background: linear-gradient(90deg, #f8fbff 0%, #ffffff 100%);
  padding: 14px 16px;
}

.main-header-left .title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.main-header-left h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.main-header-left p,
.main-header-right p {
  margin: 0;
  font-size: 12px;
  line-height: 1.8;
  color: #909399;
}

.dialog-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px;
  background: #fafbfc;
}

.message-row {
  display: flex;
  margin-bottom: 12px;
}

.message-row.user {
  justify-content: flex-start;
}

.message-row.admin {
  justify-content: flex-end;
}

.message-row .bubble {
  max-width: 72%;
  border-radius: 12px;
  padding: 10px 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-row.user .bubble {
  background: #fff;
  border: 1px solid #ebeef5;
}

.message-row.admin .bubble {
  background: #ecf5ff;
  border: 1px solid #d9ecff;
}

.bubble .sender {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 6px;
}

.bubble .content {
  // 2026-10-09：覆盖全局禁选样式，例如选中用户提问中的音效名称后复制去搜索。
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
  font-size: 14px;
  color: #303133;
  line-height: 1.6;
}

.bubble .time {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  text-align: right;
}

.dialog-editor {
  border-top: 1px solid #ebeef5;
  background: #fff;
  padding: 14px 16px;
}

.editor-actions {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #909399;
  font-size: 12px;
}

.editor-tools {
  display: flex;
  align-items: center;
}

.editor-tip {
  margin-left: 8px;
}

.pagination {
  margin-top: 12px;
}
</style>
